import { useCallback, useState } from 'react';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { SALES_DATE_FILTER_TO_CID } from 'src/utils/constants/contentIdMaps';
import {
  getDisabledDatesForTimeFrameFilter,
  StandardDateRangePresetNames,
  StandardDateRangePresetNamesV2,
} from 'src/utils/dateTimeUtils';
import { DateTimeRangeWithRelative, ListingQuery } from 'src/WebApiController';

import { StackedFilter } from './StackedFilter';

export enum SaleDateFilterEnum {
  HasSaleDate = 'HasSaleDate',
  NoSaleDate = 'NoSaleDate',
}

export const SaleDateStackedFilter = ({
  isHorizontalLayout,
}: {
  isHorizontalLayout?: boolean;
}) => {
  const { tempQuery, setTempQuery } = useFilterQueryContext<ListingQuery>();

  const [mode, setMode] = useState<SaleDateFilterEnum>(() => {
    // Set the initial mode based on tempQuery
    return tempQuery.lastSaleDates
      ? SaleDateFilterEnum.HasSaleDate
      : tempQuery.noSaleDates
        ? SaleDateFilterEnum.NoSaleDate
        : SaleDateFilterEnum.HasSaleDate;
  });

  const setDateFilter = useCallback(
    (mode: SaleDateFilterEnum, date: DateTimeRangeWithRelative | null) => {
      const newQuery: ListingQuery = {
        ...tempQuery,
        lastSaleDates: mode === SaleDateFilterEnum.HasSaleDate ? date : null,
        noSaleDates: mode === SaleDateFilterEnum.NoSaleDate ? date : null,
      };

      setTempQuery(newQuery);
      setMode(mode);
    },
    [tempQuery, setTempQuery]
  );

  return (
    <StackedFilter
      isHorizontalLayout={isHorizontalLayout}
      FilterComponents={[
        <PosEnumSelect
          key="topFilter"
          value={mode}
          valueOptionsContent={SALES_DATE_FILTER_TO_CID}
          enableEmptySelection={false}
          onChange={(mode) => {
            if (mode === SaleDateFilterEnum.HasSaleDate) {
              setDateFilter(mode, tempQuery['lastSaleDates']);
            } else if (mode === SaleDateFilterEnum.NoSaleDate) {
              setDateFilter(mode, tempQuery['noSaleDates']);
            }
          }}
        />,
        <DateRangeSelector
          key="dateRangeSelector"
          useRelativePresets={false}
          presetNames={StandardDateRangePresetNames}
          exactPresetNames={StandardDateRangePresetNamesV2}
          trailingPresetNames={StandardDateRangePresetNamesV2}
          value={tempQuery['lastSaleDates'] || tempQuery['noSaleDates']}
          defaultValue={null}
          onBlur={({ value }) => {
            setDateFilter(mode, value);
          }}
          disabledDay={(d) =>
            getDisabledDatesForTimeFrameFilter(
              d,
              tempQuery.eventTimeFrameFilter
            )
          }
          isEndDateFixed
          displayTextStyle={{ maxWidth: '260px' }}
        />,
      ]}
    />
  );
};
